"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Camera,
  CameraOff,
  ChevronDown,
  Circle,
  Loader2,
  RotateCcw,
  Send,
  Square,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "ui";

import { useVideoRecorder } from "@/lib/hooks/use-video-recorder";
import { formatMs } from "@/lib/utils";
import { uploadVideoToMux } from "@/lib/upload-video-to-mux";

interface VideoMessageRecorderProps {
  studioId: string;
  onSend: (videoId: string, videoDuration: number) => Promise<void>;
  onCancel: () => void;
}

export default function VideoMessageRecorder({
  studioId,
  onSend,
  onCancel,
}: VideoMessageRecorderProps) {
  const {
    state,
    durationMs,
    videoBlob,
    videoMimeType,
    stream,
    cameras,
    selectedCameraId,
    startPreview,
    selectCamera,
    startRecording,
    stopRecording,
    cancelRecording,
    reset,
  } = useVideoRecorder();

  const [isUploading, setIsUploading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [showCameraMenu, setShowCameraMenu] = useState(false);

  const blobUrl = useMemo(
    () => (videoBlob ? URL.createObjectURL(videoBlob) : null),
    [videoBlob],
  );

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Callback ref (not a plain ref + effect): attaches the stream the
  // instant the live-preview <video> node mounts, instead of relying on
  // a `stream` dependency that doesn't change when only `state` does.
  // Previewing and recording share this same node (see below) so the
  // camera feed never flickers when recording starts/stops.
  const attachLiveVideo = useCallback(
    (node: HTMLVideoElement | null) => {
      if (node && stream) {
        node.srcObject = stream;
      }
    },
    [stream],
  );

  // Always start preview on mount.
  useEffect(() => {
    setErrorMsg(null);
    startPreview().catch((err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "Could not access camera";
      setErrorMsg(msg);
      toast.error(msg);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReRecord = useCallback(() => {
    reset();
    setErrorMsg(null);
    startPreview().catch((err: unknown) => {
      const msg =
        err instanceof Error ? err.message : "Could not access camera";
      setErrorMsg(msg);
      toast.error(msg);
    });
  }, [reset, startPreview]);

  const handleSend = useCallback(async () => {
    if (!videoBlob) return;
    setIsUploading(true);
    try {
      const mimeType = videoMimeType || "video/webm";
      const { videoId } = await uploadVideoToMux(videoBlob, {
        filename: `video-message-${Date.now()}.${mimeType.includes("mp4") ? "mp4" : "webm"}`,
        mimeType,
      });

      await onSend(videoId, durationMs);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setIsUploading(false);
    }
  }, [videoBlob, videoMimeType, durationMs, onSend, studioId]);

  const handleCancel = useCallback(() => {
    cancelRecording();
    onCancel();
  }, [cancelRecording, onCancel]);

  const selectedCamera = cameras.find((c) => c.deviceId === selectedCameraId);

  // ── Error state ───────────────────────────────────────────────────────────

  if (errorMsg && (state === "idle" || state === "requesting")) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4">
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
          <CameraOff className="h-5 w-5 flex-shrink-0 text-destructive" />
          <span className="flex-1 text-sm text-destructive">{errorMsg}</span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
      </div>
    );
  }

  // ── Requesting state ──────────────────────────────────────────────────────

  if (state === "requesting") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">
          Accessing camera...
        </span>
      </div>
    );
  }

  // ── Stopped state — playback preview ─────────────────────────────────────

  if (state === "stopped" && videoBlob && blobUrl) {
    return (
      <div className="flex h-full flex-col gap-4">
        <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-black">
          <video
            key="stopped"
            src={blobUrl}
            controls
            playsInline
            className="h-full w-full object-contain"
            aria-label="Video message preview"
          />
          <span className="absolute left-3 top-3 rounded-full bg-black/50 px-2.5 py-1 text-xs font-medium text-white">
            {formatMs(durationMs)}
          </span>
        </div>

        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReRecord}
            disabled={isUploading}
          >
            <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
            Re-record
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={handleSend} disabled={isUploading}>
              {isUploading ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Send className="mr-1.5 h-3.5 w-3.5" />
              )}
              {isUploading ? "Sending..." : "Send"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Previewing / recording state ──────────────────────────────────────────
  // Rendered as one block (not separate branches per state) so the live
  // <video> node — and the camera stream attached to it — stays mounted
  // across the start/stop transition instead of flickering.

  const isRecording = state === "recording";

  return (
    <div className="flex h-full flex-col gap-4">
      {/* Camera selector — hidden once recording starts */}
      {!isRecording && cameras.length > 1 && (
        <div className="relative self-start">
          <button
            type="button"
            onClick={() => setShowCameraMenu((v) => !v)}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-accent"
          >
            <Camera className="h-4 w-4 text-muted-foreground" />
            <span className="max-w-[180px] truncate">
              {selectedCamera?.label || "Camera"}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          </button>

          {showCameraMenu && (
            <div className="absolute left-0 top-full z-10 mt-1 w-64 rounded-lg border border-border bg-popover py-1 shadow-lg">
              {cameras.map((cam, i) => (
                <button
                  key={cam.deviceId}
                  type="button"
                  onClick={() => {
                    setShowCameraMenu(false);
                    selectCamera(cam.deviceId);
                  }}
                  className={`w-full truncate px-3 py-2 text-left text-sm transition-colors hover:bg-accent ${
                    cam.deviceId === selectedCameraId
                      ? "font-medium text-primary"
                      : "text-foreground"
                  }`}
                >
                  {cam.label || `Camera ${i + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Live preview */}
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-xl bg-black">
        <video
          key="live"
          ref={attachLiveVideo}
          autoPlay
          muted
          playsInline
          className="h-full w-full object-cover"
        />
        {isRecording && (
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-black/50 px-3 py-1.5">
            <div className="h-2 w-2 animate-pulse rounded-full bg-destructive" />
            <span className="text-xs font-medium tabular-nums text-white">
              {formatMs(durationMs)}
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="ghost" size="sm" onClick={handleCancel}>
          Cancel
        </Button>
        {isRecording ? (
          <button
            type="button"
            onClick={stopRecording}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive text-white transition-opacity hover:opacity-80 active:scale-95"
            aria-label="Stop recording"
          >
            <Square className="h-5 w-5 fill-current" />
          </button>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-80 active:scale-95"
            aria-label="Start recording"
          >
            <Circle className="h-6 w-6 fill-current" />
          </button>
        )}
      </div>
    </div>
  );
}
